import { supabase } from '../lib/supabase';

/**
 * Profile Service
 * Handles user profile operations including photo upload, bio, links, and badges
 */

// Upload profile photo
export const uploadProfilePhoto = async (userId, file) => {
  try {
    const fileExt = file?.name?.split('.')?.pop();
    const fileName = `${userId}-${Date.now()}.${fileExt}`;
    const filePath = `${userId}/${fileName}`;

    // Upload file to storage
    const { error: uploadError } = await supabase?.storage?.from('profile-photos')?.upload(filePath, file, {
        cacheControl: '3600',
        upsert: true
      });

    if (uploadError) throw uploadError;

    return filePath;
  } catch (error) {
    console.error('Error uploading profile photo:', error);
    throw error;
  }
};

// Get profile photo URL (signed URL for private bucket)
export const getProfilePhotoUrl = async (filePath) => {
  try {
    if (!filePath) return null;

    const { data, error } = await supabase?.storage?.from('profile-photos')?.createSignedUrl(filePath, 3600); // 1 hour expiry

    if (error) throw error;
    return data?.signedUrl;
  } catch (error) {
    console.error('Error getting profile photo URL:', error);
    return null;
  }
};

// Update user profile
export const updateUserProfile = async (userId, updates) => {
  try {
    const { data, error } = await supabase?.from('user_profiles')?.update(updates)?.eq('id', userId)?.select()?.single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error updating user profile:', error);
    throw error;
  }
};

// Get user profile with photo URL
export const getUserProfile = async (userId) => {
  try {
    const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('id', userId)?.single();

    if (error) throw error;

    // Get signed URL for profile photo if exists
    if (data?.profile_photo_url) {
      data.photoUrl = await getProfilePhotoUrl(data?.profile_photo_url);
    }

    return data;
  } catch (error) {
    console.error('Error getting user profile:', error);
    throw error;
  }
};

// Get all badges
export const getAllBadges = async () => {
  try {
    const { data, error } = await supabase?.from('badges')?.select('*')?.order('category', { ascending: true });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error getting badges:', error);
    return [];
  }
};

// Get user badges
export const getUserBadges = async (userId) => {
  try {
    const { data, error } = await supabase?.from('user_badges')?.select(`
        id,
        earned_at,
        display_order,
        badge:badges(*)
      `)?.eq('user_id', userId)?.order('display_order', { ascending: true });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error getting user badges:', error);
    return [];
  }
};

// Update badge display order
export const updateBadgeOrder = async (userId, badgeOrders) => {
  try {
    // badgeOrders is an array of { id, display_order }
    const updates = badgeOrders?.map(({ id, display_order }) =>
      supabase?.from('user_badges')?.update({ display_order })?.eq('id', id)?.eq('user_id', userId)
    );

    const results = await Promise.all(updates);
    const errors = results?.filter(r => r?.error);

    if (errors?.length > 0) {
      throw new Error('Failed to update badge order');
    }

    return true;
  } catch (error) {
    console.error('Error updating badge order:', error);
    throw error;
  }
};

// Add external link
export const addExternalLink = async (userId, link) => {
  try {
    // Get current profile
    const { data: profile, error: fetchError } = await supabase?.from('user_profiles')?.select('external_links')?.eq('id', userId)?.single();

    if (fetchError) throw fetchError;

    const currentLinks = profile?.external_links || [];
    const updatedLinks = [...currentLinks, link];

    const { data, error } = await supabase?.from('user_profiles')?.update({ external_links: updatedLinks })?.eq('id', userId)?.select()?.single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error adding external link:', error);
    throw error;
  }
};

// Remove external link
export const removeExternalLink = async (userId, linkIndex) => {
  try {
    const { data: profile, error: fetchError } = await supabase?.from('user_profiles')?.select('external_links')?.eq('id', userId)?.single();

    if (fetchError) throw fetchError;

    const currentLinks = profile?.external_links || [];
    const updatedLinks = currentLinks?.filter((_, index) => index !== linkIndex);

    const { data, error } = await supabase?.from('user_profiles')?.update({ external_links: updatedLinks })?.eq('id', userId)?.select()?.single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error removing external link:', error);
    throw error;
  }
};

export default {
  uploadProfilePhoto,
  getProfilePhotoUrl,
  updateUserProfile,
  getUserProfile,
  getAllBadges,
  getUserBadges,
  updateBadgeOrder,
  addExternalLink,
  removeExternalLink
};